import { Injectable, Inject } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { tap, catchError } from "rxjs/operators";
import { IHttpService, WebLogger, UrlKey } from "@weps/shared-http";
import {
  PersonCreate,
  PersonUpdate,
  PeopleSyncRequest,
  PeopleSyncResponse,
  CreatePersonResponse,
  UpdatePersonResponse,
  GetMemberResponse,
  GetPeopleResponse,
  ZokRequestHeaders,
} from "./zok.models";

@Injectable({
  providedIn: "root",
})
export class ZokService {
  constructor(@Inject("IHttpService") private httpService: IHttpService) {}

  private logApiCall<T>(
    apiName: string,
    httpMethod: string,
    successStatusCode: number,
    getSuccessMetadata?: (response: T) => Record<string, unknown>,
    getErrorMetadata?: () => Record<string, unknown>,
  ): (source: Observable<T>) => Observable<T> {
    return (source: Observable<T>) =>
      source.pipe(
        tap((response: T) => {
          WebLogger.log({
            message: `ZOK ${apiName.replace(/_/g, " ")} succeeded`,
            level: "info",
            payload: {
              event_type: "api_call_complete",
              service: "zok",
              api_name: apiName,
              http_method: httpMethod,
              status_code: successStatusCode,
              ...(getSuccessMetadata ? getSuccessMetadata(response) : {}),
            },
          });
        }),
        catchError((error: unknown) => {
          WebLogger.log({
            message: `ZOK ${apiName.replace(/_/g, " ")} failed`,
            level: "error",
            payload: {
              event_type: "api_call_failed",
              service: "zok",
              api_name: apiName,
              http_method: httpMethod,
              error_message: error instanceof Error ? error.message : String(error),
              error_type: error instanceof Error ? error.name : "UnknownError",
              status_code: error instanceof HttpErrorResponse ? error.status : 0,
              ...(getErrorMetadata ? getErrorMetadata() : {}),
            },
          });
          return throwError(() => error);
        }),
      );
  }

  createPerson(
    person: PersonCreate,
    additionalHeaders?: Partial<ZokRequestHeaders>,
  ): Observable<CreatePersonResponse> {
    return this.httpService
      .doPostRequest<CreatePersonResponse>(UrlKey.zok_people_url, { person }, additionalHeaders)
      .pipe(
        this.logApiCall<CreatePersonResponse>("create_person", "POST", 201, (response) => ({
          household_member_id: response.person.householdMemberId,
        })),
      );
  }

  updatePerson(
    householdMemberId: string,
    person: PersonUpdate,
    additionalHeaders?: Partial<ZokRequestHeaders>,
  ): Observable<UpdatePersonResponse> {
    return this.httpService
      .doPutRequest<UpdatePersonResponse>(
        UrlKey.zok_person_update_url,
        { person },
        additionalHeaders,
        { householdMemberId },
      )
      .pipe(
        this.logApiCall<UpdatePersonResponse>(
          "update_person",
          "PUT",
          200,
          (response) => ({ household_member_id: response.person.householdMemberId }),
          () => ({ household_member_id: householdMemberId }),
        ),
      );
  }

  syncPeople(
    request: PeopleSyncRequest,
    additionalHeaders?: Partial<ZokRequestHeaders>,
  ): Observable<PeopleSyncResponse> {
    return this.httpService
      .doPostRequest<PeopleSyncResponse>(UrlKey.zok_people_sync_url, request, additionalHeaders)
      .pipe(
        this.logApiCall<PeopleSyncResponse>("sync_people", "POST", 200, (response) => ({
          household_members_count: response.householdMembers.length,
          unmatched_records_count: response.unmatchedRecords.length,
          potential_matches_count: response.potentialMatches.length,
          errors_count: response.errors.length,
        })),
      );
  }

  getPeople(additionalHeaders?: Partial<ZokRequestHeaders>): Observable<GetPeopleResponse> {
    return this.httpService
      .doGetRequest<GetPeopleResponse>(UrlKey.zok_people_url, additionalHeaders)
      .pipe(
        this.logApiCall<GetPeopleResponse>("get_people", "GET", 200, (response) => ({
          people_count: response.people.length,
        })),
      );
  }

  getMember(
    householdMemberId: string,
    additionalHeaders?: Partial<ZokRequestHeaders>,
  ): Observable<GetMemberResponse> {
    return this.httpService
      .doGetRequest<GetMemberResponse>(UrlKey.zok_member_url, additionalHeaders, {
        householdMemberId,
      })
      .pipe(
        this.logApiCall<GetMemberResponse>(
          "get_member",
          "GET",
          200,
          () => ({ household_member_id: householdMemberId }),
          () => ({ household_member_id: householdMemberId }),
        ),
      );
  }
}
